import { useGetProposalDocument } from '@/api/queries/proposal/useGetProposalDocument';
import useProposalDocumentPreview, {
  type ProposalDocumentRegion,
} from '@/ui/pages/proposalBuilderPage/useProposalDocumentPreview';
import { Badge, EmptyState, UntitledLoading } from '@payaca/ambrosia';
import ClientDocumentLoading from '@payaca/components/clientDocument/ClientDocumentLoading';
import { FC } from 'react';

type Props = {
  proposalId: string;
  /**
   * Opens the region for editing. Omit whenever the editor pane is showing
   * something other than the sections — a sent Proposal's resend view, or the
   * send composer — which leaves the preview inert.
   */
  onRegionClick?: (region: ProposalDocumentRegion) => void;
  activeSectionKey?: string | null;
};

/**
 * The Proposal document as the Customer will see it, rendered from the server's
 * markup. Clicking a section or line item opens it in the editor.
 */
const ProposalPreviewPane: FC<Props> = (props) => {
  const { proposalId, onRegionClick, activeSectionKey } = props;

  const { html, isOutOfSync, isLoading, isError } =
    useGetProposalDocument(proposalId);

  const hostRef = useProposalDocumentPreview({
    html: html ?? '',
    onRegionClick,
    activeSectionKey,
  });

  // Query errors are silent by default, so surface one rather than spinning.
  // A failed refetch keeps the last document, which is better than blanking it.
  if (isError && html === undefined) {
    return <EmptyState text="Preview unavailable" />;
  }

  if (isLoading || html === undefined) {
    return <ClientDocumentLoading summaryPlacement="stacked" />;
  }

  return (
    <div
      role="region"
      aria-label="Proposal preview"
      // Focusable so a keyboard-only user can scroll the preview column.
      tabIndex={0}
      className="pb-8"
    >
      {isOutOfSync && (
        // Zero height so it overlays the document rather than displacing it,
        // and ordered before the document so it sticks for the whole scroll.
        <div
          role="status"
          className="pointer-events-none sticky top-3 z-10 h-0 w-fit ps-3"
        >
          <Badge
            variant="white"
            size="sm"
            className="flex items-center gap-1.5"
          >
            <UntitledLoading className="size-3" />
            Syncing…
          </Badge>
        </div>
      )}
      <div ref={hostRef} />
    </div>
  );
};

export default ProposalPreviewPane;
